import React, { useState, useEffect } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../config/firebase';
import { ChevronRight } from 'lucide-react';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  secondary: '#7C3AED',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const PhoneAuth = ({ isSignUp, role, onSuccess, onBack }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Setup reCAPTCHA
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved
        }
      });
    }
  }, []);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

    try {
      const appVerifier = window.recaptchaVerifier;
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(confirmation);
      setStep('otp');
    } catch (err) {
      console.error('Error sending OTP:', err);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      await confirmationResult.confirm(otp);
      onSuccess();
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border-2 p-10" style={{ borderColor: BRAND_COLORS.primary }}>
      <div className="mb-10">
        <p className="text-xs font-light tracking-widest mb-4" style={{ color: BRAND_COLORS.accent }}>
          {step === 'phone' ? 'AUTHENTICATION' : 'VERIFICATION'}
        </p>
        <h3 className="text-3xl font-light tracking-wide mb-4" style={{ color: BRAND_COLORS.primary }}>
          {step === 'phone' 
            ? (isSignUp 
                ? `Sign Up as ${role === 'professional' ? 'Professional' : 'Employer'}` 
                : 'Sign In to SkillVend')
            : 'Enter OTP'}
        </h3>
        <p className="text-gray-600 font-light leading-relaxed">
          {step === 'phone' 
            ? (isSignUp 
                ? 'Enter your mobile number to create your account.' 
                : 'Enter your mobile number to sign in.')
            : `Code sent to +91${phoneNumber}`}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 border-2 border-red-600 bg-red-50">
          <p className="text-red-700 text-sm font-light">{error}</p>
        </div>
      )}

      {step === 'phone' ? (
        <form onSubmit={handleSendOtp} className="space-y-6">
          <div>
            <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
              Phone Number
            </label>
            <div className="flex items-center space-x-3">
              <div className="px-4 py-4 border-2 font-light" style={{ borderColor: BRAND_COLORS.lightGray }}>
                +91
              </div>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit mobile number"
                className="flex-1 px-4 py-4 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
                style={{ borderColor: BRAND_COLORS.lightGray }}
                required
              />
            </div>
          </div>

          <div id="recaptcha-container"></div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-4 border-2 font-light tracking-wide transition-all duration-300 hover:border-black"
              style={{ borderColor: BRAND_COLORS.lightGray }}
            >
              BACK
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-4 px-6 bg-black text-white font-light tracking-widest text-sm transition-all duration-300 hover:bg-opacity-90 flex items-center justify-between group"
            >
              <span>{loading ? 'SENDING...' : 'SEND OTP'}</span>
              <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-2" />
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="space-y-6">
          <div>
            <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
              6-Digit OTP
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-4 border-2 bg-transparent font-light text-2xl tracking-widest text-center transition-all duration-300 focus:outline-none focus:border-black"
              style={{ borderColor: BRAND_COLORS.lightGray }}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 px-6 bg-black text-white font-light tracking-widest text-sm transition-all duration-300 hover:bg-opacity-90 flex items-center justify-between group mt-8"
          >
            <span>{loading ? 'VERIFYING...' : 'VERIFY OTP'}</span>
            <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-2" />
          </button>

          <button
            type="button"
            onClick={() => setStep('phone')}
            className="w-full text-xs font-light tracking-widest hover:opacity-70 transition-all uppercase"
            style={{ color: BRAND_COLORS.primary }}
          >
            Change Number
          </button>
        </form>
      )}
    </div>
  );
};

export default PhoneAuth;
